import React, { useState } from "react";
import {
  Badge,
  Button,
  Calendar,
  Card,
  Checkbox,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import { CalendarOutlined, DeleteOutlined, TableOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import locale from "antd/es/date-picker/locale/nl_NL";

const OpeningHours = () => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState("calendar");
  const storeSchedule = useSelector((state) => state.firestoreReducer.ordered.storeSchedule);

  // Create a map of dates to schedules for quick lookup
  const scheduleMap = {};
  if (storeSchedule) {
    storeSchedule.forEach((schedule) => {
      scheduleMap[schedule.date] = schedule;
    });
  }

  // Check if a day is normally closed (Saturday=6, Sunday=0)
  const isNormallyClosed = (date) => {
    const day = date.day();
    return day === 0 || day === 6; // Sunday, Saturday
  };

  // Check if it's Monday (special day: only national shipping, no pickup)
  const isMonday = (date) => {
    return date.day() === 1;
  };

  // Get status for a specific date
  const getDateStatus = (date) => {
    const dateStr = date.format(DATE_FORMAT_DD_MM_YYYY);
    const schedule = scheduleMap[dateStr];
    const normallyClosed = isNormallyClosed(date);
    const monday = isMonday(date);

    if (schedule) {
      if (schedule.isOpen) {
        return { type: "success", text: t("day_open"), color: "green", isCustom: true };
      } else {
        return { type: "error", text: t("day_closed"), color: "red", isCustom: true };
      }
    }

    if (monday) {
      return { 
        type: "warning", 
        text: t("monday_limited"), 
        color: "orange", 
        isCustom: false,
        isMonday: true 
      };
    }

    if (normallyClosed) {
      return { type: "default", text: t("day_closed"), color: "gray", isCustom: false };
    }

    return { type: "success", text: t("day_open"), color: "green", isCustom: false };
  };

  const openModal = (schedule = null, date = null) => {
    setEditingSchedule(schedule);
    setSelectedDate(date);

    if (schedule) {
      form.setFieldsValue({
        isOpen: schedule.isOpen,
        reason: schedule.reason || "",
        deliveryMethods: schedule.deliveryMethods || [],
        scopes: schedule.scopes || [],
      });
    } else if (date) {
      const normallyClosed = isNormallyClosed(date);
      form.setFieldsValue({
        isOpen: !normallyClosed,
        reason: "",
        deliveryMethods: [],
        scopes: [],
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingSchedule(null);
    setSelectedDate(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const dateStr = editingSchedule ? editingSchedule.date : selectedDate.format(DATE_FORMAT_DD_MM_YYYY);

      const data = {
        date: dateStr,
        isOpen: values.isOpen,
        reason: values.reason || "",
        deliveryMethods: values.deliveryMethods || [],
        scopes: values.scopes || [],
        updatedAt: dayjs().toISOString(),
      };

      if (editingSchedule) {
        await firestore.collection("storeSchedule").doc(editingSchedule.id).update(data);
      } else {
        data.createdAt = dayjs().toISOString();
        await firestore.collection("storeSchedule").add(data);
      }

      message.success(t("schedule_saved"));
      closeModal();
    } catch (error) {
      console.error("Error saving schedule:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await firestore.collection("storeSchedule").doc(id).delete();
      message.success(t("schedule_deleted"));
    } catch (error) {
      console.error("Error deleting schedule:", error);
    }
  };

  // Render calendar cell
  const dateCellRender = (date) => {
    const dateStr = date.format(DATE_FORMAT_DD_MM_YYYY);
    const schedule = scheduleMap[dateStr];
    const status = getDateStatus(date);

    return (
      <div
        style={{
          minHeight: 60,
          padding: 4,
          cursor: "pointer",
          borderRadius: 4,
          transition: "background-color 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
      >
        <Badge status={status.type} text={status.text} />
        {status.isMonday && !schedule && (
          <div style={{ fontSize: 10, marginTop: 4, color: "#666" }}>
            <div><Tag color="green" style={{ fontSize: 8, padding: "0 2px" }}>{t("national")}</Tag> {t("send")}</div>
            <div><Tag color="red" style={{ fontSize: 8, padding: "0 2px" }}>{t("regional")}</Tag> {t("day_closed")}</div>
            <div><Tag color="red" style={{ fontSize: 8, padding: "0 2px" }}>{t("pickup")}</Tag> {t("day_closed")}</div>
          </div>
        )}
        {schedule && (
          <div style={{ fontSize: 11, marginTop: 4 }}>
            <Tag color={status.color} style={{ fontSize: 10, padding: "0 4px", marginBottom: 2 }}>
              {t("custom_schedule")}
            </Tag>
            {schedule.deliveryMethods && schedule.deliveryMethods.length > 0 && (
              <div style={{ fontSize: 9, marginTop: 2 }}>
                {schedule.deliveryMethods.map((m) => (
                  <Tag key={m} style={{ fontSize: 8, padding: "0 2px", marginTop: 1 }}>
                    {t(m === "pickup" ? "pickup" : "send")}
                  </Tag>
                ))}
              </div>
            )}
            {schedule.scopes && schedule.scopes.length > 0 && (
              <div style={{ fontSize: 9, marginTop: 2 }}>
                {schedule.scopes.map((s) => (
                  <Tag key={s} style={{ fontSize: 8, padding: "0 2px", marginTop: 1 }}>
                    {t(s)}
                  </Tag>
                ))}
              </div>
            )}
            {schedule.reason && <div style={{ color: "#666", marginTop: 2, fontSize: 10 }}>{schedule.reason}</div>}
          </div>
        )}
      </div>
    );
  };

  const onCalendarSelect = (date, info) => {
    // Only open modal when clicking on a date cell, not when changing month/year
    if (info?.source === "date") {
      const dateStr = date.format(DATE_FORMAT_DD_MM_YYYY);
      const schedule = scheduleMap[dateStr];
      if (schedule) {
        openModal(schedule);
      } else {
        openModal(null, date);
      }
    }
  };

  const headerRender = ({ value, type, onChange, onTypeChange }) => {
    const month = value.month();
    const year = value.year();
    const monthOptions = [];
    const yearOptions = [];

    for (let i = 0; i < 12; i++) {
      monthOptions.push(
        <Select.Option key={i} value={i}>
          {dayjs().month(i).format("MMMM")}
        </Select.Option>,
      );
    }

    const currentYear = dayjs().year();
    for (let i = currentYear - 1; i <= currentYear + 2; i++) {
      yearOptions.push(
        <Select.Option key={i} value={i}>
          {i}
        </Select.Option>,
      );
    }

    return (
      <div style={{ padding: "16px 24px", background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#262626" }}>
          {value.format("MMMM YYYY")}
        </div>
        <Space>
          <Select
            value={month}
            onChange={(newMonth) => {
              const now = value.clone().month(newMonth);
              onChange(now);
            }}
            style={{ width: 140 }}
          >
            {monthOptions}
          </Select>
          <Select
            value={year}
            onChange={(newYear) => {
              const now = value.clone().year(newYear);
              onChange(now);
            }}
            style={{ width: 100 }}
          >
            {yearOptions}
          </Select>
        </Space>
      </div>
    );
  };

  const columns = [
    {
      title: t("date"),
      dataIndex: "date",
      key: "date",
      render: (text) => dayjs(text, DATE_FORMAT_DD_MM_YYYY).format("ddd D MMM 'YY"),
      sorter: (a, b) => dayjs(a.date, DATE_FORMAT_DD_MM_YYYY).unix() - dayjs(b.date, DATE_FORMAT_DD_MM_YYYY).unix(),
    },
    {
      title: t("day_status"),
      key: "status",
      render: (_, record) => {
        const date = dayjs(record.date, DATE_FORMAT_DD_MM_YYYY);
        const status = getDateStatus(date);
        return <Badge status={status.type} text={status.text} />;
      },
    },
    {
      title: t("delivery_method"),
      dataIndex: "deliveryMethods",
      key: "deliveryMethods",
      render: (methods) => {
        if (!methods || methods.length === 0) return <Tag>Alle</Tag>;
        return (
          <Space size="small">
            {methods.map((m) => (
              <Tag key={m}>{t(m === "pickup" ? "pickup" : "send")}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: t("scope"),
      dataIndex: "scopes",
      key: "scopes",
      render: (scopes) => {
        if (!scopes || scopes.length === 0) return <Tag>Alle</Tag>;
        return (
          <Space size="small">
            {scopes.map((s) => (
              <Tag key={s}>{t(s)}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: t("reason"),
      dataIndex: "reason",
      key: "reason",
      render: (text) => text || "-",
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openModal(record)}>
            {t("edit")}
          </Button>
          <Popconfirm
            title={t("delete_schedule")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button type="link" danger>
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>{t("opening_hours_management")}</h2>
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            {
              label: t("calendar_view"),
              value: "calendar",
              icon: <CalendarOutlined />,
            },
            {
              label: t("list_view"),
              value: "list",
              icon: <TableOutlined />,
            },
          ]}
        />
      </div>

      {viewMode === "calendar" ? (
        <Calendar
          dateCellRender={dateCellRender}
          onSelect={onCalendarSelect}
          headerRender={headerRender}
          locale={locale}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={storeSchedule}
          rowKey="id"
          locale={{ emptyText: t("table_no_schedules") }}
        />
      )}

      <Modal
        title={
          editingSchedule
            ? t("edit_opening_hours")
            : selectedDate
              ? `${t("edit_opening_hours")} - ${selectedDate.format("D MMMM YYYY")}`
              : t("edit_opening_hours")
        }
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={closeModal}
        okText={t("save")}
        cancelText={t("cancel")}
        width={500}
        footer={[
          editingSchedule && (
            <Popconfirm
              key="delete"
              title={t("delete_schedule")}
              onConfirm={() => {
                handleDelete(editingSchedule.id);
                closeModal();
              }}
              okText={t("yes")}
              cancelText={t("no")}
            >
              <Button danger icon={<DeleteOutlined />} type="link">
                {t("delete")}
              </Button>
            </Popconfirm>
          ),
          <Button key="cancel" onClick={closeModal}>
            {t("cancel")}
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {t("save")}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="isOpen" label={t("day_status")}>
            <Radio.Group>
              <Radio value={true}>{t("store_is_open")}</Radio>
              <Radio value={false}>{t("store_is_closed")}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="deliveryMethods" label={t("delivery_method")} tooltip="Laat leeg voor alle verzendmethoden">
            <Checkbox.Group
              options={[
                { label: t("pickup"), value: "pickup" },
                { label: t("send"), value: "send" },
              ]}
            />
          </Form.Item>

          <Form.Item name="scopes" label={t("scope")} tooltip="Laat leeg voor alle bereiken">
            <Checkbox.Group
              options={[
                { label: t("regional"), value: "regional" },
                { label: t("national"), value: "national" },
              ]}
            />
          </Form.Item>

          <Form.Item name="reason" label={t("reason_optional")}>
            <Input.TextArea rows={2} placeholder={t("reason_placeholder")} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default OpeningHours;
